import React, { useState, useEffect } from 'react';
import { questionAPI, answerAPI, quizAPI, courseAPI, topicAPI, cacheUtils, topicLessonAPI, losAPI } from '../../services/api';
import { DataTable } from '../ui/data-table';
import { DefaultEditor } from 'react-simple-wysiwyg';
import FileUpload from '../ui/FileUpload';
import * as XLSX from 'xlsx';

const Questions = () => {
  const [questions, setQuestions] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [topics, setTopics] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [los, setLos] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAnswersModalOpen, setIsAnswersModalOpen] = useState(false);
  const [selectedQuestionAnswers, setSelectedQuestionAnswers] = useState([]);
  const [selectedQuestionText, setSelectedQuestionText] = useState('');
  const [loading, setLoading] = useState(false);
  const [quizzesLoading, setQuizzesLoading] = useState(false);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [losLoading, setLosLoading] = useState(false);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [deletingQuestionId, setDeletingQuestionId] = useState(null);
  const [selectedLosId, setSelectedLosId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ current: 0, total: 0, status: '' });
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [formData, setFormData] = useState({
    quiz_id: '' || 0, 
    course_id: '',
    topic_id: '',
    lesson_id: '',
    text: '',
    question_type: 1, // multiple_choice = 1, true_false = 2, short_answer = 3
    order_index: 1,
    los_id : null,
    image : null,
    difficulty : 'easy',
    answers: [
      { answer_text: '', is_correct: 0, order_index: 1 },
      { answer_text: '', is_correct: 0, order_index: 2 },
      { answer_text: '', is_correct: 0, order_index: 3 },
      { answer_text: '', is_correct: 0, order_index: 4 }
    ]
  });
  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      fetchQuizzes();
      fetchCourses();
    }
  }, [isModalOpen]);

  // Handle question type changes to adjust answers array
  useEffect(() => {
    if (formData.question_type === 3) {
      // Short answer - ensure we have only one answer entry
      setFormData(prev => ({
        ...prev,
        answers: [
          { answer_text: prev.answers[0]?.answer_text || '', is_correct: 1, order_index: 1 }
        ]
      }));
    } else if (formData.question_type === 2) {
      // True/False - ensure we have exactly 2 answers
      setFormData(prev => ({
        ...prev,
        answers: [
          { answer_text: 'True', is_correct: 0, order_index: 1 },
          { answer_text: 'False', is_correct: 0, order_index: 2 }
        ]
      }));
    } else {
      // Multiple choice - ensure we have at least 4 answers if currently less
      setFormData(prev => {
        if (prev.answers.length < 4) {
          const newAnswers = [...prev.answers];
          while (newAnswers.length < 4) {
            newAnswers.push({ 
              answer_text: '', 
              is_correct: 0, 
              order_index: newAnswers.length + 1 
            });
          }
          return { ...prev, answers: newAnswers };
        }
        return prev;
      });
    }
  }, [formData.question_type]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await questionAPI.getAllQuestions();
      setQuestions(response.data || []);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuizzes = async () => {
    try {
      setQuizzesLoading(true);
      const response = await quizAPI.getAllQuizzes();
      setQuizzes(response.data || []);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      setQuizzesLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const response = await courseAPI.getAllCourses();
      setCourses(response.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchTopics = async (courseId) => {
    if (!courseId) {
      setTopics([]);
      setLessons([]);
      return;
    }
    
    try {
      setTopicsLoading(true);
      const response = await topicAPI.getTopicsByCourseId(courseId);
      setTopics(response.data || []);
      setLessons([]); // Reset lessons when course changes
    } catch (error) {
      console.error('Error fetching topics:', error);
      setTopics([]);
    } finally {
      setTopicsLoading(false);
    }
  };

  const fetchLessons = async (topicId) => {
    if (!topicId) {
      setLessons([]);
      return;
    }
    
    try {
      setLessonsLoading(true);
      const response = await topicLessonAPI.getTopicLessonsByTopicId(topicId);
      setLessons(response.data || []);
    } catch (error) {
      console.error('Error fetching lessons:', error);
      setLessons([]);
    } finally {
      setLessonsLoading(false);
    }
  };

  const fetchlos = async (lessonId) => {
    if (!lessonId) {
      setLos([]);
      return;
    }
    try {
      setLosLoading(true);
      const response = await losAPI.getLOSByLessonId(lessonId);
      setLos(response.data || []);
    } catch (error) {
      console.error('Error fetching LOS:', error);
      setLos([]);
    }
    finally {
      setLosLoading(false);
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle question type changes immediately
    if (name === 'question_type') {
      const questionType = parseInt(value);
      setFormData(prev => {
        let newAnswers;
        
        if (questionType === 3) {
          // Short answer - single answer entry
          newAnswers = [
            { answer_text: '', is_correct: 1, order_index: 1 }
          ];
        } else if (questionType === 2) {
          // True/False - exactly 2 answers
          newAnswers = [
            { answer_text: 'True', is_correct: 0, order_index: 1 },
            { answer_text: 'False', is_correct: 0, order_index: 2 }
          ];
        } else {
          // Multiple choice - at least 4 answers
          newAnswers = [
            { answer_text: '', is_correct: 0, order_index: 1 },
            { answer_text: '', is_correct: 0, order_index: 2 },
            { answer_text: '', is_correct: 0, order_index: 3 },
            { answer_text: '', is_correct: 0, order_index: 4 }
          ];
        }
        
        return {
          ...prev,
          [name]: questionType,
          answers: newAnswers
        };
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Handle course change - fetch topics and lessons
    if (name === 'course_id') {
      setFormData(prev => ({
        ...prev,
        topic_id: '',
        lesson_id: ''
      }));
      fetchTopics(value);
    }

    // Handle topic change - reset lesson
    if (name === 'topic_id') {
      setFormData(prev => ({
        ...prev,
        lesson_id: '',
        los_id: null
      }));
      fetchLessons(value);
    }

    // Handle lesson change - fetch LOS
    if (name === 'lesson_id') {
      setFormData(prev => ({
        ...prev,
        los_id: null
      }));
      fetchlos(value);
    }
  };

  const handleEditorChange = (e) => {
    setFormData(prev => ({
      ...prev,
      text: e.target.value
    }));
  };

  const handleImageUploadSuccess = (url, key) => {
    setFormData(prev => ({
      ...prev,
      image: url
    }));
  };

  const handleImageUploadError = (error) => {
    console.error('Image upload error:', error);
    alert(`Failed to upload image: ${error}`);
  };

  const handleAnswerChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      answers: prev.answers.map((answer, i) => 
        i === index ? { ...answer, [field]: value } : answer
      )
    }));
  };

  const handleCorrectAnswerChange = (index) => {
    setFormData(prev => ({
      ...prev,
      answers: prev.answers.map((answer, i) => ({
        ...answer,
        is_correct: i === index ? 1 : 0, // Only the selected answer should be correct (1), others should be incorrect (0)
        order_index: i + 1
      }))
    }));
  };

  const addAnswer = () => {
    setFormData(prev => ({
      ...prev,
      answers: [...prev.answers, { 
        answer_text: '', 
        is_correct: 0, 
        order_index: prev.answers.length + 1 
      }]
    }));
  };

  const removeAnswer = (index) => {
    if (formData.answers.length > 2) {
      setFormData(prev => ({
        ...prev,
        answers: prev.answers.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Different validation for short answer vs multiple choice/true-false
      if (formData.question_type === 3) {
        // Short answer questions don't require correct answer validation
        // They can have an optional reference answer
      } else {
        // Validate that at least one answer is marked as correct for MC/TF questions
        const hasCorrectAnswer = formData.answers.some(answer => answer.is_correct);
        if (!hasCorrectAnswer) {
          alert('Please mark at least one answer as correct.');
          return;
        }

        // Filter out empty answers
        const validAnswers = formData.answers.filter(answer => answer.answer_text.trim() !== '');
        
        if (validAnswers.length < 2) {
          alert('Please provide at least 2 answers for multiple choice/true-false questions.');
          return;
        }
      }

      // For short answer questions, filter answers differently
      const validAnswers = formData.question_type === 3 
        ? formData.answers.filter(answer => answer.answer_text && answer.answer_text.trim() !== '').slice(0, 1) // Only take first answer for reference
        : formData.answers.filter(answer => answer.answer_text.trim() !== '');

      const questionData = {
        quiz_id: parseInt(formData.quiz_id) || 1,
        lesson_id: parseInt(formData.lesson_id) || 0,
        text: formData.text,
        question_type: parseInt(formData.question_type) || 1,
        order_index: parseInt(formData.order_index) || 1,
        los_id: formData.los_id ? parseInt(formData.los_id) : null,
        image: formData.image,
        difficulty: formData.difficulty,
      };

      let questionId;
      
      if (isEditing) {
        // Update existing question
        questionData.id = editingQuestionId;
        console.log('Updating question:', questionData);
        await questionAPI.updateQuestion(questionData);
        questionId = editingQuestionId;
        
        // Delete existing answers for this question
        const answersResponse = await answerAPI.getAnswersByQuestionId(questionId);
        const existingAnswers = answersResponse.data || [];
        for (const answer of existingAnswers) {
          await answerAPI.deleteAnswer(answer.id);
        }
      } else {
        // Create new question
        console.log('Creating question:', questionData);
        const questionResponse = await questionAPI.createQuestion(questionData);
        questionId = questionResponse.data.id;
      }

      // Create answers for the question
      if (formData.question_type === 3) {
        // For short answer questions, create only reference answer if provided
        if (validAnswers.length > 0) {
          const answerData = {
            question_id: questionId,
            option_text: validAnswers[0].answer_text,
            is_correct: 1, // Mark as correct for reference
            order_index: 1
          };
          console.log('Creating reference answer for short answer question:', answerData);
          await answerAPI.createAnswer(answerData);
        }
      } else {
        // For multiple choice/true-false questions, create all answers
        for (const answer of validAnswers) {
          const answerData = {
            question_id: questionId,
            option_text: answer.answer_text,
            is_correct: !answer.is_correct ? 0 : answer.is_correct,
            order_index: answer.order_index
          };
          console.log('Creating answer:', answerData);
          await answerAPI.createAnswer(answerData);
        }
      }

      setIsModalOpen(false);
      resetForm();
      fetchQuestions();
      cacheUtils.clearQuestionCache();
      cacheUtils.clearAnswerCache();
    } catch (error) {
      console.error(`Error ${isEditing ? 'updating' : 'creating'} question:`, error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} question. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestion = async (question) => {
    try {
      setLoading(true);
      
      // Fetch answers for this question
      const answersResponse = await answerAPI.getAnswersByQuestionId(question.id);
      const answers = answersResponse.data || [];
      
      // If course/topic/lesson are set, fetch the related data
      if (question.lesson_id) {
        // We need to find the course and topic for this lesson
        // First fetch all courses to find which course contains this lesson
        const coursesResponse = await courseAPI.getAllCourses();
        const allCourses = coursesResponse.data || [];
        
        let foundCourse = null;
        let foundTopic = null;
        
        for (const course of allCourses) {
          const topicsResponse = await topicAPI.getTopicsByCourseId(course.id);
          const topics = topicsResponse.data || [];
          
          for (const topic of topics) {
            const lessonsResponse = await topicLessonAPI.getTopicLessonsByTopicId(topic.id);
            const lessons = lessonsResponse.data || [];
            
            if (lessons.some(l => l.lesson_id === question.lesson_id)) {
              foundCourse = course;
              foundTopic = topic;
              break;
            }
          }
          if (foundCourse) break;
        }
        
        if (foundCourse) {
          await fetchTopics(foundCourse.id);
          if (foundTopic) {
            await fetchLessons(foundTopic.id);
          }
          if (question.lesson_id) {
            await fetchlos(question.lesson_id);
          }
        }
      }
      
      // Map answers to the format expected by the form
      let formattedAnswers;
      if (question.question_type === 3) {
        // Short answer - single answer
        formattedAnswers = answers.length > 0 
          ? [{ answer_text: answers[0].option_text, is_correct: 1, order_index: 1 }]
          : [{ answer_text: '', is_correct: 1, order_index: 1 }];
      } else if (question.question_type === 2) {
        // True/False - 2 answers
        formattedAnswers = answers.length >= 2
          ? answers.slice(0, 2).map(a => ({
              answer_text: a.option_text,
              is_correct: a.is_correct,
              order_index: a.order_index
            }))
          : [
              { answer_text: 'True', is_correct: 0, order_index: 1 },
              { answer_text: 'False', is_correct: 0, order_index: 2 }
            ];
      } else {
        // Multiple choice - at least 4 answers
        formattedAnswers = answers.map(a => ({
          answer_text: a.option_text,
          is_correct: a.is_correct,
          order_index: a.order_index
        }));
        
        // Ensure at least 4 answers
        while (formattedAnswers.length < 4) {
          formattedAnswers.push({
            answer_text: '',
            is_correct: 0,
            order_index: formattedAnswers.length + 1
          });
        }
      }
      
      setFormData({
        quiz_id: question.quiz_id || '',
        course_id: '', // We'll populate this if we found it
        topic_id: '', // We'll populate this if we found it
        lesson_id: question.lesson_id || '',
        text: question.text || '',
        question_type: question.question_type || 1,
        order_index: question.order_index || 1,
        los_id: question.los_id || null,
        image: question.image || null,
        difficulty: question.difficulty || 'easy',
        answers: formattedAnswers
      });
      
      setIsEditing(true);
      setEditingQuestionId(question.id);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error loading question for edit:', error);
      alert('Failed to load question data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question? This will also delete all associated answers.')) {
      return;
    }

    try {
      setDeletingQuestionId(questionId);
      console.log('Deleting question with ID:', questionId);
      
      // First get all answers for this question and delete them
      const answersResponse = await answerAPI.getAnswersByQuestionId(questionId);
      const answers = answersResponse.data || [];
      
      // Delete all answers
      for (const answer of answers) {
        await answerAPI.deleteAnswer(answer.id);
      }
      
      // Then delete the question
      await questionAPI.deleteQuestion(questionId);
      
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question. Please try again.');
    } finally {
      setDeletingQuestionId(null);
    }
  };

  const handleViewAnswers = async (questionId, questionText) => {
    try {
      setAnswersLoading(true);
      setSelectedQuestionText(questionText);
      console.log('Fetching answers for question ID:', questionId);
      
      const response = await answerAPI.getAnswersByQuestionId(questionId);
      setSelectedQuestionAnswers(response.data || []);
      setIsAnswersModalOpen(true);
    } catch (error) {
      console.error('Error fetching answers:', error);
      alert('Failed to load answers. Please try again.');
    } finally {
      setAnswersLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      quiz_id: 1,
      course_id: '',
      topic_id: '',
      lesson_id: '',
      text: '',
      question_type: 1,
      order_index: 1,
      los_id: null,
      image: null,
      difficulty: 'easy',
      answers: [
        { answer_text: '', is_correct: 0, order_index: 1 },
        { answer_text: '', is_correct: 0, order_index: 2 },
        { answer_text: '', is_correct: 0, order_index: 3 },
        { answer_text: '', is_correct: 0, order_index: 4 }
      ]
    });
    // Reset dependent data
    setTopics([]);
    setLessons([]);
    setLos([]);
    setIsEditing(false);
    setEditingQuestionId(null);
  };

  // Bulk Upload Functions
  const downloadExampleFile = () => {
    const exampleData = [
      {
        'Quiz ID': 1,
        'Question Text': 'What is the capital of France?',
        'Question Type': 'multiple_choice',
        'Difficulty': 'easy',
        'Order Index': 1,
        'Lesson ID': '',
        'LOS ID': '',
        'Answer 1': 'Paris',
        'Answer 1 Correct': 'TRUE',
        'Answer 2': 'London',
        'Answer 2 Correct': 'FALSE',
        'Answer 3': 'Berlin',
        'Answer 3 Correct': 'FALSE',
        'Answer 4': 'Madrid',
        'Answer 4 Correct': 'FALSE'
      },
      {
        'Quiz ID': 1,
        'Question Text': 'Is the Earth round?',
        'Question Type': 'true_false',
        'Difficulty': 'easy',
        'Order Index': 2,
        'Lesson ID': '',
        'LOS ID': '',
        'Answer 1': 'True',
        'Answer 1 Correct': 'TRUE',
        'Answer 2': 'False',
        'Answer 2 Correct': 'FALSE',
        'Answer 3': '',
        'Answer 3 Correct': '',
        'Answer 4': '',
        'Answer 4 Correct': ''
      },
      {
        'Quiz ID': 1,
        'Question Text': 'What is 2 + 2?',
        'Question Type': 'short_answer',
        'Difficulty': 'easy',
        'Order Index': 3,
        'Lesson ID': '',
        'LOS ID': '',
        'Answer 1': '4',
        'Answer 1 Correct': 'TRUE',
        'Answer 2': '',
        'Answer 2 Correct': '',
        'Answer 3': '',
        'Answer 3 Correct': '',
        'Answer 4': '',
        'Answer 4 Correct': ''
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(exampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Questions');
    
    // Set column widths
    worksheet['!cols'] = [
      { wch: 10 },  // Quiz ID
      { wch: 50 },  // Question Text
      { wch: 20 },  // Question Type
      { wch: 12 },  // Difficulty
      { wch: 12 },  // Order Index
      { wch: 10 },  // Lesson ID
      { wch: 10 },  // LOS ID
      { wch: 30 },  // Answer 1
      { wch: 15 },  // Answer 1 Correct
      { wch: 30 },  // Answer 2
      { wch: 15 },  // Answer 2 Correct
      { wch: 30 },  // Answer 3
      { wch: 15 },  // Answer 3 Correct
      { wch: 30 },  // Answer 4
      { wch: 15 }   // Answer 4 Correct
    ];

    XLSX.writeFile(workbook, 'questions_bulk_upload_template.xlsx');
  };

  const handleBulkUploadFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBulkUploadFile(file);
    }
  };

  const parseQuestionType = (typeString) => {
    const type = typeString?.toLowerCase().trim();
    if (type === 'multiple_choice' || type === 'multiple choice') return 1;
    if (type === 'true_false' || type === 'true/false' || type === 'truefalse') return 2;
    if (type === 'short_answer' || type === 'short answer') return 3;
    return 1; // default to multiple choice
  };

  const handleBulkUpload = async () => {
    if (!bulkUploadFile) {
      alert('Please select a file to upload.');
      return;
    }

    try {
      setIsBulkUploading(true);
      setBulkUploadProgress({ current: 0, total: 0, status: 'Reading file...' });

      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            alert('The Excel file is empty or improperly formatted.');
            setIsBulkUploading(false);
            return;
          }

          setBulkUploadProgress({ 
            current: 0, 
            total: jsonData.length, 
            status: `Processing ${jsonData.length} questions...` 
          });

          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            
            try {
              setBulkUploadProgress({ 
                current: i + 1, 
                total: jsonData.length, 
                status: `Creating question ${i + 1} of ${jsonData.length}...` 
              });

              // Validate required fields
              if (!row['Quiz ID'] || !row['Question Text']) {
                errors.push(`Row ${i + 1}: Missing required fields (Quiz ID or Question Text)`);
                errorCount++;
                continue;
              }

              const questionType = parseQuestionType(row['Question Type'] || 'multiple_choice');
              
              // Create question
              const questionData = {
                quiz_id: parseInt(row['Quiz ID']),
                text: row['Question Text'],
                question_type: questionType,
                difficulty: row['Difficulty']?.toLowerCase() || 'easy',
                order_index: parseInt(row['Order Index']) || i + 1,
                lesson_id: row['Lesson ID'] ? parseInt(row['Lesson ID']) : null,
                los_id: row['LOS ID'] ? parseInt(row['LOS ID']) : null,
                image: null
              };

              console.log(`Creating question ${i + 1}:`, questionData);
              const questionResponse = await questionAPI.createQuestion(questionData);
              const questionId = questionResponse.data?.id || questionResponse.id;

              if (!questionId) {
                errors.push(`Row ${i + 1}: Failed to get question ID from response`);
                errorCount++;
                continue;
              }

              // Create answers
              const answers = [];
              for (let j = 1; j <= 4; j++) {
                const answerText = row[`Answer ${j}`];
                const isCorrect = row[`Answer ${j} Correct`];
                
                if (answerText && answerText.toString().trim() !== '') {
                  answers.push({
                    question_id: questionId,
                    option_text: answerText.toString().trim(),
                    is_correct: isCorrect?.toString().toUpperCase() === 'TRUE' ? 1 : 0,
                    order_index: j
                  });
                }
              }

              // For short answer and true/false, validate answer count
              if (questionType === 3 && answers.length === 0) {
                errors.push(`Row ${i + 1}: Short answer question must have at least one answer`);
                errorCount++;
                continue;
              }

              if (questionType === 2 && answers.length !== 2) {
                errors.push(`Row ${i + 1}: True/False question must have exactly 2 answers`);
                errorCount++;
                continue;
              }

              // Create all answers
              for (const answer of answers) {
                await answerAPI.createAnswer(answer);
              }

              successCount++;
              console.log(`✓ Successfully created question ${i + 1} with ${answers.length} answers`);

            } catch (rowError) {
              console.error(`Error processing row ${i + 1}:`, rowError);
              errors.push(`Row ${i + 1}: ${rowError.message || 'Unknown error'}`);
              errorCount++;
            }
          }

          // Clear cache after bulk upload
          cacheUtils.clearQuestionCache();
          cacheUtils.clearAnswerCache();

          // Refresh questions list
          await fetchQuestions();

          // Show results
          let resultMessage = `Bulk upload completed!\n\n`;
          resultMessage += `✓ Successfully created: ${successCount} questions\n`;
          if (errorCount > 0) {
            resultMessage += `✗ Failed: ${errorCount} questions\n\n`;
            if (errors.length > 0) {
              resultMessage += `Errors:\n${errors.slice(0, 10).join('\n')}`;
              if (errors.length > 10) {
                resultMessage += `\n... and ${errors.length - 10} more errors`;
              }
            }
          }

          alert(resultMessage);

          // Close modal and reset
          setIsBulkUploadModalOpen(false);
          setBulkUploadFile(null);
          setBulkUploadProgress({ current: 0, total: 0, status: '' });

        } catch (error) {
          console.error('Error parsing Excel file:', error);
          alert(`Failed to parse Excel file: ${error.message}`);
        } finally {
          setIsBulkUploading(false);
        }
      };

      reader.readAsArrayBuffer(bulkUploadFile);

    } catch (error) {
      console.error('Error in bulk upload:', error);
      alert(`Bulk upload failed: ${error.message}`);
      setIsBulkUploading(false);
    }
  };

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      className: 'w-16',
    },
    {
      accessorKey: 'text',
      header: 'Question',
      className: 'w-96',
      cell: (row) => (
        <div className="max-w-96 text-sm leading-tight">
          <div dangerouslySetInnerHTML={{ __html: row.text }} />
        </div>
      )
    },
    {
      accessorKey: 'question_type',
      header: 'Type',
      className: 'w-48',
      cell: (row) => (
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          {row.question_type === 1 ? 'Multiple Choice' : row.question_type === 2 ? 'True/False' : row.question_type === 3 ? 'Short Answer' : 'N/A'}
        </span>
      )
    },
    {
      accessorKey: 'order_index',
      header: 'Order Index',
      className: 'w-20',
    },
    {
      accessorKey: 'quiz_id',
      header: 'Quiz ID',
      className: 'w-20',
    },
    {
      accessorKey: 'lesson_id',
      header: 'Lesson ID',
      className: 'w-20',
      cell: (row) => row.lesson_id || 'N/A'
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-2">
          <button
            onClick={() => handleViewAnswers(row.id, row.text)}
            disabled={answersLoading}
            className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="View answers"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          <button
            onClick={() => handleEditQuestion(row)}
            disabled={loading}
            className="p-2 text-purple-500 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Update question"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteQuestion(row.id)}
            disabled={deletingQuestionId === row.id}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete question"
          >
            {deletingQuestionId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Question Bank</h1>
            <p className="text-lg text-gray-600">Create and manage quiz questions with answers</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + Add Question
            </button>
            <button
              onClick={() => setIsBulkUploadModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              📤 Bulk Upload
            </button>
            <button
              onClick={downloadExampleFile}
              className="px-4 py-2 text-sm bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
            >
              📥 Download Template
            </button>
            <button
              onClick={() => {
                cacheUtils.clearQuestionCache();
                fetchQuestions();
              }}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => cacheUtils.clearAllCache()}
              className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              🗑️ Clear Cache
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={questions}
        searchKey="text"
        searchPlaceholder="Search questions..."
        loading={loading}
        emptyMessage="No questions found. Create your first question!"
        pageSize={10}
      />

      {/* Question Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Update Question' : 'Create New Question'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quiz Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quiz
                </label>
                <select
                  name="quiz_id"
                  value={formData.quiz_id}
                  onChange={handleInputChange}
                  disabled={quizzesLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                >
                  <option value="">
                    {quizzesLoading ? 'Loading quizzes...' : 'Select a quiz'}
                  </option>
                  {!quizzesLoading && quizzes.map((quiz) => (
                    <option key={quiz.id} value={quiz.id}>
                      {quiz.title || `Quiz ${quiz.id}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Course Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Course (Optional - for lesson association)
                </label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleInputChange}
                  disabled={coursesLoading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                >
                  <option value="">
                    {coursesLoading ? 'Loading courses...' : 'Select a course (optional)'}
                  </option>
                  {!coursesLoading && courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.title || `Course ${course.id}`}
                    </option>
                  ))}
                </select>
              </div>

              {/* Topic and Lesson Selection - Only show if course is selected */}
              {formData.course_id && (
                <div className="grid grid-cols-2 gap-4">
                  {/* Topic Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Topic
                    </label>
                    <select
                      name="topic_id"
                      value={formData.topic_id}
                      onChange={handleInputChange}
                      disabled={topicsLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    >
                      <option value="">
                        {topicsLoading ? 'Loading topics...' : 'Select a topic (optional)'}
                      </option>
                      {!topicsLoading && topics.map((topic) => (
                        <option key={topic.id} value={topic.id}>
                          {topic.title || `Topic ${topic.id}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Lesson Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lesson
                    </label>
                    <select
                      name="lesson_id"
                      value={formData.lesson_id}
                      onChange={handleInputChange}
                      disabled={lessonsLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    >
                      <option value="">
                        {lessonsLoading ? 'Loading lessons...' : 'Select a lesson (optional)'}
                      </option>
                      {!lessonsLoading && lessons
                        .filter(lesson => !formData.topic_id || lesson.topic_id === parseInt(formData.topic_id))
                        .map((lesson) => (    
                        <option key={lesson.lesson_id} value={lesson.lesson_id}>
                          {lesson.lesson_name || lesson.name || `Lesson ${lesson.id}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* LOS Selection - Only show if lesson is selected */}
              {formData.lesson_id && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Learning Outcome Standard (LOS)
                    </label>
                    <select
                      name="los_id"
                      value={formData.los_id || ''}
                      onChange={handleInputChange}
                      disabled={losLoading}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                    >
                      <option value="">
                        {losLoading ? 'Loading LOS...' : 'Select a learning outcome (optional)'}
                      </option>
                      {!losLoading && los
                        .filter(lo => lo.lesson_id === parseInt(formData.lesson_id))
                        .map((lo) => (
                        <option key={lo.id} value={lo.id}>
                          {lo.name || lo.description || `LOS ${lo.id}`}
                        </option>
                      ))}
                    </select>
                    {los.length === 0 && formData.lesson_id && !losLoading && (
                      <p className="text-xs text-gray-400 mt-1">
                        No learning outcomes found for this lesson.
                      </p>
                    )}
                  </div>
                )}

              {/* Question Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Text
                </label>
                <div className="mt-1">
                  <DefaultEditor
                    value={formData.text}
                    onChange={handleEditorChange}
                    containerProps={{
                      style: {
                        minHeight: '200px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px'
                      }
                    }}
                    editorProps={{
                      disabled: loading,
                      placeholder: 'Enter your question here...'
                    }}
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Use the rich text editor to format your question with styling, images, and links.
                </p>
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question Image (Optional)
                </label>
                <FileUpload
                  onUploadSuccess={handleImageUploadSuccess}
                  onUploadError={handleImageUploadError}
                  accept="image/*"
                  maxSize={5 * 1024 * 1024} // 5MB
                  folder="question-images"
                  className="w-full"
                  disabled={loading}
                  showPreview={true}
                  currentUrl={formData.image}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Upload an image to accompany your question (JPG, PNG, GIF supported, max 5MB)
                </p>
              </div>

              {/* Difficulty Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Difficulty Level
                </label>
                <select
                  name="difficulty"
                  value={formData.difficulty}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                  <option value="very hard">Very Hard</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select the difficulty level for this question
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Question Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Question Type
                  </label>
                  <select
                    name="question_type"
                    value={formData.question_type}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="1">Multiple Choice</option>
                    <option value="2">True/False</option>
                    <option value="3">Short Answer</option>
                  </select>
                </div>

                {/* Points */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order Index
                  </label>
                  <input
                    type="number"
                    name="order_index"
                    value={formData.order_index || 0}
                    onChange={handleInputChange}
                    min="1"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Answers Section */}
              <div>
                {formData.question_type === 3 ? (
                  // Short Answer Question
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-4">
                      Expected Answer (for reference only)
                    </label>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={formData.answers[0]?.answer_text || ''}
                        onChange={(e) => handleAnswerChange(0, 'answer_text', e.target.value)}
                        placeholder="Enter the expected answer or key points (optional)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500">
                        This is for reference only. Short answer questions will be graded manually.
                      </p>
                    </div>
                  </div>
                ) : (
                  // Multiple Choice / True-False Questions
                  <div>
                    <div className="flex justify-between items-center mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Answer Options
                      </label>
                      <button
                        type="button"
                        onClick={addAnswer}
                        className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      >
                        + Add Answer
                      </button>
                    </div>
                    
                    <div className="space-y-3">
                      {formData.answers.map((answer, index) => (
                        <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-md">
                          <input
                            type="radio"
                            name="correct_answer"
                            checked={answer.is_correct}
                            onChange={() => handleCorrectAnswerChange(index)}
                            className="text-green-600 focus:ring-green-500"
                            title="Mark as correct answer"
                          />
                          <input
                            type="text"
                            value={answer.answer_text}
                            onChange={(e) => handleAnswerChange(index, 'answer_text', e.target.value)}
                            placeholder={`Answer option ${index + 1}`}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          {formData.answers.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeAnswer(index)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                              title="Remove answer"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Click the radio button to mark the correct answer. At least 2 answers are required.
                  </p>
                </div>
              )}
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsModalOpen(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {loading 
                    ? (isEditing ? 'Updating...' : 'Creating...') 
                    : (isEditing ? 'Update Question' : 'Create Question')
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Answers Modal */}
      {isAnswersModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Question Answers</h2>
              <button
                onClick={() => {
                  setIsAnswersModalOpen(false);
                  setSelectedQuestionAnswers([]);
                  setSelectedQuestionText('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Question Text */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Question:</h3>
              <p className="text-gray-900">{selectedQuestionText}</p>
            </div>

            {/* Answers List */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Answer Options ({selectedQuestionAnswers.length}):
              </h3>
              
              {answersLoading ? (
                <div className="flex items-center justify-center p-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : selectedQuestionAnswers.length > 0 ? (
                selectedQuestionAnswers
                  .sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
                  .map((answer, index) => (
                    <div 
                      key={answer.id || index} 
                      className={`p-4 border rounded-lg ${
                        answer.is_correct 
                          ? 'border-green-500 bg-green-50' 
                          : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                            {answer.order_index || index + 1}
                          </span>
                          <p className="text-gray-900 flex-1">{answer.option_text || answer.answer_text}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {answer.is_correct && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                              </svg>
                              Correct
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No answers found for this question.
                </div>
              )}
            </div>

            <div className="flex justify-end mt-6 pt-4 border-t">
              <button
                onClick={() => {
                  setIsAnswersModalOpen(false);
                  setSelectedQuestionAnswers([]);
                  setSelectedQuestionText('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {isBulkUploadModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                Bulk Upload Questions
              </h2>
              <button
                onClick={() => {
                  setIsBulkUploadModalOpen(false);
                  setBulkUploadFile(null);
                  setBulkUploadProgress({ current: 0, total: 0, status: '' });
                }}
                disabled={isBulkUploading}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">📋 Instructions:</h3>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Download the template file using the "Download Template" button</li>
                  <li>Fill in your questions following the template format</li>
                  <li>Question Types: multiple_choice, true_false, or short_answer</li>
                  <li>Mark correct answers with TRUE in the "Answer X Correct" columns</li>
                  <li>Save and upload your completed Excel file</li>
                </ol>
              </div>

              {/* Template Download */}
              <div className="flex items-center justify-between p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <div>
                  <h4 className="text-sm font-medium text-purple-900">Need a template?</h4>
                  <p className="text-xs text-purple-700 mt-1">Download the example file to get started</p>
                </div>
                <button
                  onClick={downloadExampleFile}
                  className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors"
                >
                  📥 Download Template
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Excel File
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleBulkUploadFileChange}
                  disabled={isBulkUploading}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100 disabled:opacity-50"
                />
                {bulkUploadFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ Selected: {bulkUploadFile.name}
                  </p>
                )}
              </div>

              {/* Progress Indicator */}
              {isBulkUploading && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {bulkUploadProgress.status}
                    </span>
                    <span className="text-sm text-gray-600">
                      {bulkUploadProgress.current} / {bulkUploadProgress.total}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full transition-all duration-300"
                      style={{
                        width: `${bulkUploadProgress.total > 0 
                          ? (bulkUploadProgress.current / bulkUploadProgress.total) * 100 
                          : 0}%`
                      }}
                    ></div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setIsBulkUploadModalOpen(false);
                    setBulkUploadFile(null);
                    setBulkUploadProgress({ current: 0, total: 0, status: '' });
                  }}
                  disabled={isBulkUploading}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={!bulkUploadFile || isBulkUploading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isBulkUploading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Uploading...
                    </span>
                  ) : (
                    '📤 Upload Questions'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questions;
